import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Play } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Input, Modal, Select, Table, message } from "antd";
import axios from "axios";

import { logOutUser } from "../../../common";
import {
  AUTH_TOKEN,
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API_DID_URL,
  REACT_APP_PAM_API,
} from "../../../constants";
import { useDebounce } from "../../../common/debounce";
import { formatTimeByTimezone } from "../../../common/formatTimebyTimezone";
import { AN_COLORS } from "../../../design-system/tokens";
import {
  EmbeddedShell,
  FilterCard,
  FilterField,
  MonoCell,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";
import { listSessionRecording } from "../../../service/sshProxyService";
import { EMPTY, orDash } from "../../../pages/enduser/ssh-proxy/sshProxyModel";

// Screen recordings — the Guacamole web-terminal flow.
//
// These rows predate the SSH proxy and describe themselves differently: no
// sessionId, no targetHost/targetAccount, and the bytes live behind a presigned
// recordingUrl rather than coming through getRecordingContent. A row with no
// recordingLength has nothing to play, which is why View is inert there rather
// than opening an empty viewer.
const FILTER_FIELDS = [
  { value: "userName", label: "Endpoint user", source: "endpointUser" },
  { value: "endpoint", label: "Endpoint", source: "endpoint" },
  { value: "emailAddress", label: "User", source: "users" },
];

const PAGESIZE = 10;

const Dash = () => <span style={{ color: AN_COLORS.muted }}>{EMPTY}</span>;

const hasRecording = (row) => {
  const len = row?.recordingLength;
  return !!len && String(len).trim() !== "";
};

const ScreenRecording = () => {
  const [searchParams] = useSearchParams();
  const epmUser = searchParams.get("epmUser");

  const [notification, notificationCtx] = message.useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [sessionData, setSessionData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // ?endpoint= arrives from the Endpoints table's Recordings column.
  const deepEndpoint = searchParams.get("endpoint");
  const [filterBy, setFilterBy] = useState(deepEndpoint ? "endpoint" : "");
  const [filterValue, setFilterValue] = useState(deepEndpoint || "");

  const [endpoint, setEndpoint] = useState([]);
  const [endpointUser, setEndpointUser] = useState([]);
  const [userData, setUserData] = useState([]);

  const [viewingUrl, setViewingUrl] = useState(null);

  const debouncedSearch = useDebounce(search, 500);
  const activeField = FILTER_FIELDS.find((f) => f.value === filterBy);

  const fetchSessionRecording = useCallback(() => {
    setIsLoading(true);
    listSessionRecording({
      payload: {
        orgId: ORG_ID(),
        domainId: DOMAIN_ID(),
        pageId: page,
        pageSize: PAGESIZE,
        search: debouncedSearch,
        // A deep link from an endpoint user pins the filter; the picker below
        // is for everything else.
        filter: {
          filterBy: epmUser ? "username" : filterBy,
          value: epmUser ?? filterValue,
          type: "recordingmimetype",
          recordingMimeType: "guac",
        },
      },
      onSuccess: (data) => {
        setIsLoading(false);
        setTotalCount(data?.total ?? 0);
        setSessionData((data?.sessionRecordings ?? []).map((r, i) => ({ ...r, _rowId: r.id ?? i })));
      },
      onFailure: (err) => {
        setIsLoading(false);
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
          notification.open({ type: "error", content: "Credentials are invalid" });
          logOutUser();
        } else {
          notification.open({ type: "error", content: "Unable to fetch session recordings" });
        }
      },
    });
  }, [page, debouncedSearch, filterBy, filterValue, epmUser, notification]);

  useEffect(() => {
    fetchSessionRecording();
  }, [fetchSessionRecording]);

  useEffect(() => {
    const headers = { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } };

    axios
      .post(
        `${REACT_APP_PAM_API}/instances/list`,
        {
          domainId: DOMAIN_ID(),
          pageId: 1,
          pageSize: 100,
          search: "",
          filter: { filterBy: "", value: "" },
          token: AUTH_TOKEN(),
          orgId: ORG_ID(),
          MachineType: "endpoint",
        },
        headers,
      )
      .then((res) =>
        setEndpoint((res?.data?.instances || []).map((i) => ({ value: i.hostName, label: `${i.hostName} (${i.publicIp})` }))),
      )
      .catch(() => {});

    axios
      .post(
        `${REACT_APP_PAM_API}/endpointUsers/listAllEpmUsers`,
        {
          domainId: DOMAIN_ID(),
          pageId: 1,
          pageSize: 100,
          token: AUTH_TOKEN(),
          orgId: ORG_ID(),
          filter: { filterBy: "", value: "" },
          instanceIds: [],
          search: "",
        },
        headers,
      )
      .then((res) => setEndpointUser((res?.data?.epmUsers || []).map((u) => ({ value: u.userName, label: u.userName }))))
      .catch(() => {});

    axios
      .post(
        `${REACT_APP_API_DID_URL}/walletService/walletUserList`,
        { domainId: DOMAIN_ID(), pageNumber: 1, pageSize: 150, filter: "", orgId: ORG_ID() },
        headers,
      )
      .then((res) =>
        setUserData((res?.data?.users || []).filter((u) => u.emailAddress).map((u) => ({ value: u.emailAddress, label: u.emailAddress }))),
      )
      .catch(() => {});
  }, []);

  const filterOptions = useMemo(() => {
    if (activeField?.source === "endpoint") return endpoint;
    if (activeField?.source === "endpointUser") return endpointUser;
    if (activeField?.source === "users") return userData;
    return [];
  }, [activeField, endpoint, endpointUser, userData]);

  const columns = [
    {
      // First, so it's the first thing on the row.
      title: "Recording",
      key: "view",
      width: 90,
      render: (_, r) =>
        hasRecording(r) ? (
          <button
            type="button"
            onClick={() => setViewingUrl(r.recordingUrl)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "none", padding: 0, color: AN_COLORS.primary, cursor: "pointer", fontSize: 13 }}
          >
            <Play size={12} fill="currentColor" />
            Play
          </button>
        ) : (
          // Nothing was captured, so there is nothing to open. A live-looking
          // link that opens an empty viewer reads as a broken player.
          <span style={{ fontSize: 13, color: AN_COLORS.muted }} title="No recording was captured for this session">
            —
          </span>
        ),
    },
    {
      title: "Session time",
      key: "sessionRecordingTime",
      width: 200,
      render: (_, r) =>
        r.sessionRecordingTime ? (
          <span style={{ fontSize: 12.5 }}>{formatTimeByTimezone(r.sessionRecordingTime, localStorage.getItem("timezone"))}</span>
        ) : (
          <Dash />
        ),
    },
    {
      title: "Endpoint user",
      key: "userName",
      render: (_, r) => <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink }}>{orDash(r.userName)}</span>,
    },
    { title: "Endpoint", key: "endpoint", render: (_, r) => <MonoCell>{r.endpoint}</MonoCell> },
    {
      title: "User",
      key: "emailAddress",
      render: (_, r) =>
        r.emailAddress ? <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>{r.emailAddress}</span> : <Dash />,
    },
    {
      title: "Session length",
      key: "recordingLength",
      width: 140,
      render: (_, r) => (hasRecording(r) ? <MonoCell>{String(r.recordingLength).trim()}</MonoCell> : <Dash />),
    },
  ];

  const applied = [
    epmUser && { key: "deep", label: `Endpoint user: ${epmUser}` },
    !epmUser && filterBy && filterValue && {
      key: "field",
      label: `${activeField?.label}: ${filterValue}`,
      clear: () => {
        setFilterBy("");
        setFilterValue("");
      },
    },
    debouncedSearch && { key: "search", label: `Search: ${debouncedSearch}`, clear: () => setSearch("") },
  ].filter(Boolean);

  return (
    <EmbeddedShell>
      {notificationCtx}

      <FilterCard
        columns="1fr 1.2fr 1.4fr"
        applied={applied}
        onClearAll={() => {
          setFilterBy("");
          setFilterValue("");
          setSearch("");
          setPage(1);
        }}
        showing={sessionData.length}
        total={totalCount}
      >
        <FilterField label="Filter by">
          <Select
            allowClear
            placeholder="Field"
            disabled={!!epmUser}
            value={filterBy || undefined}
            onChange={(v) => {
              setFilterBy(v || "");
              setFilterValue("");
              setPage(1);
            }}
            options={FILTER_FIELDS.map((f) => ({ value: f.value, label: f.label }))}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>

        <FilterField label="Value">
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder={filterBy ? "Select a value" : "Pick a field first"}
            disabled={!filterBy || !!epmUser}
            value={filterValue || undefined}
            onChange={(v) => {
              setFilterValue(v || "");
              setPage(1);
            }}
            options={filterOptions}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>

        <FilterField label="Search">
          <Input
            placeholder="Search"
            allowClear
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ height: 36 }}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={sessionData}
          rowKey="_rowId"
          loading={isLoading}
          locale={tableEmpty(isLoading, applied.length ? "No recordings match your filters." : "No screen recordings found.")}
          pagination={{
            current: page,
            pageSize: PAGESIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </TableCard>

      {viewingUrl && (
        <Modal title="Screen recording" centered open width={960} footer={null} onCancel={() => setViewingUrl(null)} destroyOnClose>
          <object
            type="text/html"
            data={viewingUrl}
            width="100%"
            height="540px"
            style={{ overflow: "auto", background: "var(--an-surface)" }}
            aria-label="Screen recording"
          />
        </Modal>
      )}
    </EmbeddedShell>
  );
};

export default ScreenRecording;
